import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//components
import DataView from 'Components/DataView';
import { Button } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';

//Views
import CreateEditPartNumber from 'Modules/manufacturers/.components/partNumbersTab/.components/createEditPartNumber';
import DeletePartNumbers from 'Modules/manufacturers/.components/partNumbersTab/.components/deletePartNumber';
//Helpers
import { getDateFormatted, idsValidation, getPMUser } from 'Core/data/Helpers';
//Actions
import { partNumberCreateEditOpenModal, partNumberOpenModal, searchEntityRequest, clearRedux } from 'Redux/actions';

//Styles
import './index.scss';

//Permission
import { HasPermission } from "Core/security"

const PartNumbersTab = (props) => {
    const { height, manufacturer } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const openModalCreatePartNumber = useSelector(state => state.createEditPartNumberRedux.openModal);
    const openModalDeletePartNumber = useSelector(state => state.deletePartNumberRedux.openModal);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const user = useSelector(state => state.securityRedux.user);
    const lang = useSelector(state => state.commonRedux.language);

    const [columns, setColumns] = useState([
        {
            key: "partNumber",
            title: messages['partnumber_tab_label'],
            field: "partNumber",
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.partNumber',
            code: "partnumber_tab_label",
            render: (item) => {
                return <span>{item?.partNumber}</span>
            }
        },
        {
            key: "deviceModel_name",
            field: "deviceModel { id name }",
            title: messages['partnumber_tab_device_model'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.deviceModel?.name',
            code: "partnumber_tab_device_model",
            render: (item) => {
                return <span>{item?.deviceModel?.name}</span>
            }
        },
        {
            key: "network",
            title: messages['partnumber_tab_network'],
            field: "network",
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.network',
            code: "partnumber_tab_network",
            render: (item) => {
                return <span>{item?.network}</span>
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['partnumber_tab_created_at'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "partnumber_tab_created_at",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['partnumber_tab_created_by'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
            `,
            code: "partnumber_tab_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        },
    ])
    const [searchFields, setSearchFields] = useState(["partNumber"]);

    useEffect(() => {
        (!pmUsers || pmUsers.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, []);

    useEffect(() => {
        if (pmUsers.length > 0) {

            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                functionString: item.key === "createdBy" ? `
                        let pmUsers = ${JSON.stringify(pmUsers)}
                        pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                        return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy 
                `: item.functionString
            }))



            setColumns([...currentColumns])
        }
    }, [pmUsers, lang])

    return (
        <div style={{ marginTop: 10 }}>
            <DataView
                ref={tableRef}
                exportName="partnumbers"
                columns={columns}
                dinamicQuery
                name={`pm-partnumbers-tab-${manufacturer?.name}`}
                entity="part-numbers-tab"
                graphqlFunction="searchPartNumbers"
                graphqlSort="PartNumberSortInput"
                graphqlConditions="PartNumberSearchInput"
                customQuery="id  deviceModelId"
                hideAllInPagination
                //search
                asyncSelect
                searchConditions={["partNumber"]}
                searchOptionValue="id"
                searchOptionLabel="partNumber"
                customSearch={{ fields: ["deviceModel_manufacturerId"], terms: [manufacturer?.id], exact: true }}
                onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                    let currentConditions = {}
                    let currentTags = {}
                    if (!conditions && tags) {
                        currentConditions = tableRef?.current?.conditions;
                        currentTags = tags;
                    } else {
                        currentConditions = conditions || [];
                        currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                        if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                    }
                    currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'partNumber' && item?.fields[0] !== 'deviceModel_manufacturerId');

                    let tagsById = currentTags.filter((item) => typeof item.id === "number");
                    if (tagsById?.length > 0) {
                        //New Items
                        let currentIds = tagsById.map((item) => (item.id));
                        if (currentIds.length > 0) {
                            currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                        }
                    }
                    let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                    if (tagsByLabel.length > 0) {
                        let currentLabels = tagsByLabel.map((item) => (item.partNumber))
                        if (currentLabels.length > 0) {
                            currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                        }
                    }

                    currentConditions && currentConditions.push({ fields: ["deviceModel_manufacturerId"], terms: [manufacturer?.id], exact: true, conditionOperator: "AND" });
                    if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                    else tableRef?.current?.getData(null, rows, sort, currentConditions);
                }}
                searchFields={searchFields}
                noSplit
                onChangeColumns={(items) => setColumns(items)}
                tableButtons={["export", 'orderColumn']}
                noFilters
                height={height}
                options={[
                    {
                        id: "edit-part-number",
                        label: messages['edit'],
                        show: true,
                        disabled: !HasPermission(user, "partn", "u"),
                        single: true,
                        action: (items) => {
                            dispatch(partNumberCreateEditOpenModal(items[0]));
                        }
                    },
                    {
                        id: "delete-part-number",
                        label: messages['delete'],
                        show: true,
                        disabled: !HasPermission(user, "partn", "d"),
                        single: true,
                        action: (items) => {
                            dispatch(partNumberOpenModal(items[0]));
                        }
                    },
                ]}
                extraButton={() => {
                    return (
                        <div style={{ paddingLeft: 2 }}>
                            <Button
                                onClick={() => dispatch(partNumberCreateEditOpenModal())}
                                className="btn-dark-blue"
                                disabled={!HasPermission(user, "partn", "c")}
                                style={{ height: 42 }}
                            >
                                {messages['partnumber_tab_add_new']}
                            </Button>
                        </div>
                    )
                }}
                menuOptions={() => {
                    return [
                        messages['edit'],
                        messages['delete']
                    ]
                }}
                onClickMenu={(event, item) => {
                    if (event === messages['edit']) {
                        if (HasPermission(user, "partn", "u")) {
                            dispatch(partNumberCreateEditOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['edit'],
                                3000,
                            );
                        }
                    }
                    if (event === messages['delete']) {
                        if (HasPermission(user, "partn", "d")) {
                            dispatch(partNumberOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['delete'],
                                3000,
                            );
                        }
                    }
                }}
            />
            {openModalCreatePartNumber && <CreateEditPartNumber manufacturer={manufacturer} tableRef={tableRef} />}
            {openModalDeletePartNumber && <DeletePartNumbers tableRef={tableRef} />}
        </div>
    )
}

export default PartNumbersTab